/**
 * Cross-domain token cookie helpers. Mirrors platform-core-iac's web app so the
 * `token` cookie is written to `.signatureaviation.com` and shared across every
 * Signature app (the mechanism behind SSO). On localhost we scope it to the host.
 */
import { TOKEN_COOKIE_NAME, isLocalhost } from "./config";

/** Build the `; domain=.example.com` suffix (empty on localhost). */
function domainSuffix(): string {
  if (isLocalhost() || typeof window === "undefined") return "";
  const parts = window.location.hostname.split(".");
  // sub.domain.com -> .domain.com ; domain.com -> .domain.com
  const base = parts.length > 2 ? parts.slice(-2).join(".") : window.location.hostname;
  return `; domain=.${base}`;
}

export function getTokenCookie(): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie
    .split("; ")
    .find((c) => c.startsWith(`${TOKEN_COOKIE_NAME}=`));
  if (!match) return null;
  const raw = match.slice(TOKEN_COOKIE_NAME.length + 1);
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

/** Persist the SSO token. Default TTL 12h; the JWT's own `exp` is authoritative. */
export function setTokenCookie(token: string, ttlMs = 12 * 60 * 60 * 1000): void {
  if (typeof document === "undefined") return;
  const expires = new Date(Date.now() + ttlMs).toUTCString();
  const secure = isLocalhost() ? "" : "; Secure";
  document.cookie =
    `${TOKEN_COOKIE_NAME}=${encodeURIComponent(token)}` +
    `; expires=${expires}; path=/; SameSite=Lax${secure}${domainSuffix()}`;
}

export function clearTokenCookie(): void {
  if (typeof document === "undefined") return;
  const epoch = "Thu, 01 Jan 1970 00:00:00 GMT";
  // Clear both the apex-domain and host-only variants to be safe.
  document.cookie = `${TOKEN_COOKIE_NAME}=; expires=${epoch}; path=/; SameSite=Lax${domainSuffix()}`;
  document.cookie = `${TOKEN_COOKIE_NAME}=; expires=${epoch}; path=/; SameSite=Lax`;
}

/** Parse `#access_token=…&id_token=…` style hash fragments into a map. */
export function parseHashParams(hash: string): Record<string, string> {
  const clean = hash.startsWith("#") ? hash.slice(1) : hash;
  const out: Record<string, string> = {};
  if (!clean) return out;
  for (const pair of clean.split("&")) {
    const [k, v] = pair.split("=");
    if (k) out[decodeURIComponent(k)] = decodeURIComponent(v ?? "");
  }
  return out;
}
